/**
 * PostHog driver (web + Capacitor native).
 *
 * On web, dynamically imports `posthog-js` so the SDK isn't part of the initial
 * bundle when analytics is disabled.
 *
 * On Capacitor (iOS/Android), routes to the native `@capawesome/capacitor-posthog`
 * plugin which uses the platform SDKs under the hood. The public API surface
 * (identify/group/capture/pageview) is identical so call sites don't branch.
 */

import { Capacitor } from '@capacitor/core';

import type {
  AnalyticsConfig,
  AnalyticsDriver,
  EventProperties,
} from './types';

interface PostHogJsLike {
  init: (key: string, opts: Record<string, unknown>) => unknown;
  identify: (id: string, props?: EventProperties) => void;
  group: (type: string, key: string, props?: EventProperties) => void;
  capture: (event: string, props?: EventProperties) => void;
  reset: () => void;
}

interface NativePosthogPlugin {
  setup: (opts: { apiKey: string; host: string }) => Promise<void>;
  identify: (opts: {
    distinctId: string;
    userProperties?: EventProperties;
  }) => Promise<void>;
  group: (opts: {
    type: string;
    key: string;
    groupProperties?: EventProperties;
  }) => Promise<void>;
  capture: (opts: { event: string; properties?: EventProperties }) => Promise<void>;
  screen: (opts: { screenTitle: string; properties?: EventProperties }) => Promise<void>;
  reset: () => Promise<void>;
}

class PostHogDriver implements AnalyticsDriver {
  private web: PostHogJsLike | null = null;
  private native: NativePosthogPlugin | null = null;
  private ready = false;

  async init(config: AnalyticsConfig): Promise<void> {
    if (!config.apiKey || !config.tenantOptIn) {
      // Tenant has analytics disabled, or no key configured — stay no-op.
      this.ready = false;
      return;
    }

    if (Capacitor.isNativePlatform()) {
      try {
        const mod = await import('@capawesome/capacitor-posthog');
        // Plugin name varies between releases; both shapes are supported.
        this.native = (mod as { Posthog?: NativePosthogPlugin }).Posthog
          ?? (mod as { PostHog?: NativePosthogPlugin }).PostHog
          ?? null;
        if (this.native) {
          await this.native.setup({ apiKey: config.apiKey, host: config.host });
          this.ready = true;
        }
      } catch (err) {
        console.warn('[analytics] failed to init native PostHog', err);
        this.ready = false;
      }
      return;
    }

    try {
      const mod = await import('posthog-js');
      this.web = (mod.default as PostHogJsLike) ?? (mod as unknown as PostHogJsLike);
      this.web.init(config.apiKey, {
        api_host: config.host,
        // PII safety: don't autocapture form text content; do not record sessions.
        autocapture: {
          dom_event_allowlist: ['click', 'change', 'submit'],
        },
        mask_all_text: false,
        mask_personal_data_properties: true,
        disable_session_recording: true,
        capture_pageview: false, // we drive pageviews manually from the router
        capture_pageleave: true,
        person_profiles: 'identified_only',
        // Strip $set properties that look like PII (extra belt-and-braces).
        sanitize_properties: (props: Record<string, unknown>) => {
          const cleaned = { ...props };
          for (const key of Object.keys(cleaned)) {
            if (/email|password|first_name|last_name|full_name/i.test(key)) {
              delete cleaned[key];
            }
          }
          return cleaned;
        },
      });
      this.ready = true;
    } catch (err) {
      console.warn('[analytics] failed to init posthog-js', err);
      this.ready = false;
    }
  }

  identify(userId: string, props: EventProperties = {}): void {
    if (!this.ready) return;
    if (this.native) {
      void this.native.identify({ distinctId: userId, userProperties: props });
      return;
    }
    this.web?.identify(userId, props);
  }

  group(tenantId: string, props: EventProperties = {}): void {
    if (!this.ready) return;
    if (this.native) {
      void this.native.group({ type: 'tenant', key: tenantId, groupProperties: props });
      return;
    }
    this.web?.group('tenant', tenantId, props);
  }

  capture(event: string, props: EventProperties = {}): void {
    if (!this.ready) return;
    if (this.native) {
      void this.native.capture({ event, properties: props });
      return;
    }
    this.web?.capture(event, props);
  }

  pageview(path: string): void {
    if (!this.ready) return;
    if (this.native) {
      void this.native.screen({ screenTitle: path, properties: { $current_url: path } });
      return;
    }
    this.web?.capture('$pageview', { $current_url: path });
  }

  reset(): void {
    if (!this.ready) return;
    if (this.native) {
      void this.native.reset();
      return;
    }
    this.web?.reset();
  }

  isReady(): boolean {
    return this.ready;
  }
}

export function createPostHogDriver(): AnalyticsDriver {
  return new PostHogDriver();
}
